import * as apicall from "../components/Apicall";

// Inventory upgrade API (warehouses, item classes / prices / kits / tracking, suppliers, purchase
// orders, goods receipts, landed cost, purchase returns, transfers, stock takes, lost sales, reorder,
// exchange deposits, stock reports). Ids are plain numbers, like the rest of core Inventory.
// apicall.get throws on a non-2xx answer; apicall.post resolves with { success:false, message }.

const qs = (params = {}) => {
  const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""));
  const s = new URLSearchParams(clean).toString();
  return s ? `?${s}` : "";
};

// settings + what the caller may do: { ...flags, can: { manage, approve, purchase, count } }
export const getUpgradeSettings = () => apicall.get("inventory/upgrade/settings");
export const saveUpgradeSettings = (data) => apicall.post(data, "inventory/upgrade/settings");

// warehouses and stock by warehouse
export const listWarehouses = (params = {}) => apicall.get(`inventory/warehouses${qs(params)}`);
export const saveWarehouse = (id, data) => apicall.post(data, id ? `inventory/warehouses/${id}` : "inventory/warehouses");
export const getBalances = (params = {}) => apicall.get(`inventory/balances${qs(params)}`);
export const postStockMove = (data) => apicall.post(data, "inventory/stock-moves");

// item master
export const listItemClasses = () => apicall.get("inventory/item-classes");
export const saveItemClass = (id, data) => apicall.post(data, id ? `inventory/item-classes/${id}` : "inventory/item-classes");
export const listPriceLevels = () => apicall.get("inventory/price-levels");
export const savePriceLevel = (data) => apicall.post(data, "inventory/price-levels");
export const searchItems = (params = {}) => apicall.get(`inventory/item-search${qs(params)}`);
export const getItemExtras = (id) => apicall.get(`inventory/items/${id}/extras`);
export const saveItemExtras = (id, data) => apicall.post(data, `inventory/items/${id}/extras`);
export const saveItemPrices = (id, prices) => apicall.post({ prices }, `inventory/items/${id}/prices`);
export const saveItemKit = (id, components) => apicall.post({ components }, `inventory/items/${id}/kit`);
export const saveItemSuppliers = (id, suppliers) => apicall.post({ suppliers }, `inventory/items/${id}/suppliers`);
export const saveStockLevels = (id, levels) => apicall.post({ levels }, `inventory/items/${id}/stock-levels`);
export const getItemTracking = (id, warehouseId) => apicall.get(`inventory/items/${id}/tracking${qs({ warehouse_id: warehouseId })}`);
export const findSerial = (serialNo) => apicall.get(`inventory/serials/${encodeURIComponent(serialNo)}`);
export const getExpiring = (params = {}) => apicall.get(`inventory/expiring${qs(params)}`);
export const getLotAges = (params = {}) => apicall.get(`inventory/lot-ages${qs(params)}`);

// suppliers
export const listSuppliers = (params = {}) => apicall.get(`inventory/suppliers${qs(params)}`);
export const saveSupplier = (id, data) => apicall.post(data, id ? `inventory/suppliers/${id}` : "inventory/suppliers");

// purchase orders
export const listPurchaseOrders = (params = {}) => apicall.get(`inventory/purchase-orders${qs(params)}`);
export const getPurchaseOrder = (id) => apicall.get(`inventory/purchase-orders/${id}`);
export const savePurchaseOrder = (id, data) => apicall.post(data, id ? `inventory/purchase-orders/${id}` : "inventory/purchase-orders");
export const purchaseOrderAction = (id, action, data = {}) => apicall.post(data, `inventory/purchase-orders/${id}/${action}`);

// goods receipts, landed cost, returns
export const listReceipts = (params = {}) => apicall.get(`inventory/receipts${qs(params)}`);
export const getReceipt = (id) => apicall.get(`inventory/receipts/${id}`);
export const receiveGoods = (data) => apicall.post(data, "inventory/receipts");
export const addLandedCost = (receiptId, data) => apicall.post(data, `inventory/receipts/${receiptId}/landed-costs`);
export const returnToSupplier = (receiptId, data) => apicall.post(data, `inventory/receipts/${receiptId}/returns`);
export const listPurchaseReturns = () => apicall.get("inventory/purchase-returns");

// transfers
export const listTransfers = () => apicall.get("inventory/transfers");
export const getTransfer = (id) => apicall.get(`inventory/transfers/${id}`);
export const postTransfer = (data) => apicall.post(data, "inventory/transfers");

// stock takes
export const listCounts = () => apicall.get("inventory/counts");
export const getCount = (id) => apicall.get(`inventory/counts/${id}`);
export const startCount = (data) => apicall.post(data, "inventory/counts");
export const enterCounts = (id, data) => apicall.post(data, `inventory/counts/${id}/entries`);
export const countAction = (id, action) => apicall.post({}, `inventory/counts/${id}/${action}`);

// demand
export const listLostSales = (params = {}) => apicall.get(`inventory/lost-sales${qs(params)}`);
export const recordLostSale = (data) => apicall.post(data, "inventory/lost-sales");
export const getReorder = (params = {}) => apicall.get(`inventory/reorder${qs(params)}`);
export const ordersFromReorder = (data) => apicall.post(data, "inventory/reorder/orders");

// exchange deposits
export const listCoreDeposits = (params = {}) => apicall.get(`inventory/core-deposits${qs(params)}`);
export const coreDepositAction = (id, action, data = {}) => apicall.post(data, `inventory/core-deposits/${id}/${action}`);

// reports
export const getStockValue = (params = {}) => apicall.get(`inventory/stock-value${qs(params)}`);
export const getMovementSummary = (params = {}) => apicall.get(`inventory/movement-summary${qs(params)}`);
